/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import HPITemplateTable from "components/tables/HPITemplateTable";
import useHttp from "hooks/http.hook";
import { FC, useEffect, useState } from "react";
import { TemplateInterface, QueryResultInterface, TemplateTypeEnum } from "@malmur-technologies/medicalsystem-shared";
import { CoreAPI_URLs } from "constants/http.const";
import Box from "components/generals/Box";
import { useTranslation } from "react-i18next";
import { TagsInterface } from "interfaces";
import useSort from "hooks/useSort.hook";

/**
 * Props for the HPITemplatesDataContent component.
 * @interface HpiTemplatesDataContentProps
 * @property {(selectedHpi: TemplateInterface) => void} [selectFunction] - Callback when an HPI template is selected
 * @property {boolean} [readOnly] - Whether the table should be read-only
 * @property {TagsInterface[]} [filterTag] - Array of tags to filter templates by
 */
interface HpiTemplatesDataContentProps {
  selectFunction?: (selectedHpi: TemplateInterface) => void;
  readOnly?: boolean;
  filterTag?: TagsInterface[];
}

/**
 * Content component for displaying and managing History of Present Illness (HPI) templates.
 * Shows a table of HPI templates that can be filtered by tags and selected for use in progress notes.
 * @param {HpiTemplatesDataContentProps} props - Component props
 * @returns {React.ReactElement} The HPI templates content component
 */
const HPITemplatesDataContent: FC<HpiTemplatesDataContentProps> = ({
  selectFunction,
  readOnly,
  filterTag,
}) => {
  const [_isLoading, _error, sendRequest] = useHttp();
  const [hpitemplateList, setHpitemplateList] = useState<TemplateInterface[]>([]);
  const [page, setPage] = useState<number>(1);
  const [pageTotal, setPageTotal] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(5);
  const [rowsTotal, setRowsTotal] = useState<number>(0);
  const [templateFilter, setTemplateFilter] = useState<string>("");
  const { t } = useTranslation();
  const { sortBy, sortOrder, handleSortChange, buildSortParam } = useSort();
  useEffect(() => {
    const titleParam = templateFilter ? `title=${templateFilter}&` : "";
    const url = `${CoreAPI_URLs.HPI_TEMPLATE_GET_ALL}?type=HPI&${titleParam}${buildSortParam()}page=${page}&pageSize=${pageSize}`;
    sendRequest(url, onGetHPITemplateHandler, "GET");
    console.log("Fetching HPI templates with URL:", url);
  }, [page, pageSize, templateFilter, filterTag, buildSortParam]);

  const onGetHPITemplateHandler = (
    hpitemplatesResponse: QueryResultInterface<TemplateInterface>
  ) => {
    let results = hpitemplatesResponse.results;
    let total = hpitemplatesResponse.total;
    results = results.filter(
      (template) => template.type.trim().toUpperCase() === TemplateTypeEnum.HPI
    );
    if (filterTag && filterTag.length > 0) {
      results = results.filter((template) =>
        filterTag.every((tag) => template.tags?.includes(tag.name))
      );
      total = results.length;
    }
    setHpitemplateList(results);
    setRowsTotal(total);
    setPageTotal(Math.ceil(total / pageSize));
  };

  return (
    <Box>
      <HPITemplateTable
        modelList={hpitemplateList}
        setModelList={setHpitemplateList}
        canSearchPlaceholder={t("hpitemplatesDataContent.searchHPITemplate")}
        entriesPerPage={{ defaultValue: pageSize }}
        setPageState={setPage}
        setPageSizeState={setPageSize}
        apiPagination={true}
        apiPageTotal={pageTotal}
        apiRowsTotal={rowsTotal}
        apiPageCurrentIndex={page}
        selectFunction={selectFunction}
        readOnlyValue={readOnly}
        setModelFilter={setTemplateFilter}
        filterTag={filterTag}
        onSortChange={handleSortChange}
        currentSortBy={sortBy}
        currentSortOrder={sortOrder}
      />
    </Box>
  );
};

export default HPITemplatesDataContent;
